import './styles/InputField.scss';

import { Eye, EyeSlash, X } from '@phosphor-icons/react';
import {
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  memo,
  MemoExoticComponent,
  ReactNode,
  useState,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import { useClassHelper } from '../../hooks';
import { colors } from '../../styles/variables';
import { appTypes } from '../../types';
import { InputErrorMessage } from '../InputErrorMessage';
import { Paragraph } from '../Typography';
import { Label } from './components/Label';
const rootClass = 'c-CustomInputField';

const variants = {
  default: '',
  grouped: `${rootClass}--grouped`,
} as const;

type InputTypes = Extract<
  HTMLInputTypeAttribute,
  'text' | 'tel' | 'password' | 'number' | 'date' | 'email' | 'search' | 'url'
>;

interface CommonProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string | ReactNode;
  icon?: ReactNode;
  classes?: {
    container?: string;
    input?: string;
  };
  error?: boolean;
  errorMessage?: string;
  variant?: keyof typeof variants;
  regex?: RegExp;
  touched?: any; // This was passed in prev component but not used, to prevent build error I added this into the Props interface.
  app?: keyof typeof appTypes;
  endInputContent?: ReactNode;
  description?: string;
}

interface SearchInputProps extends CommonProps {
  type: 'search';
  onClear: () => void;
}

interface RegularInputProps extends CommonProps {
  type: Exclude<InputTypes, 'search'>;
  onClear?: () => void; // `onClear` is not allowed for types other than "search"
}

type Props = SearchInputProps | RegularInputProps;

interface InputActionButtonProps {
  inputType: string;
  isActive: boolean;
  onClick: () => void;
  name: string;
  disabled?: boolean;
  dataCy?: string;
}

const PasswordToggle = ({
  isActive,
  onClick,
  name,
  disabled,
  dataCy,
}: Pick<
  InputActionButtonProps,
  'onClick' | 'isActive' | 'name' | 'disabled' | 'dataCy'
>) => {
  const inactiveText = 'Show';
  const activeText = 'Hide';
  const c = useClassHelper(rootClass);

  return (
    <button
      id={`toggle-password-${name}`}
      className={`${c(
        '__toggle-password',
      )} focus-visible:focus-ring-form-inset`}
      type="button"
      role="switch"
      aria-checked={isActive}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      data-cy={dataCy}
    >
      {isActive ? (
        <>
          {activeText}
          <EyeSlash
            aria-label="hide password icon"
            size={24}
            color={colors.informative}
          />
        </>
      ) : (
        <>
          {inactiveText}
          <Eye
            aria-label="show password icon"
            size={24}
            color={colors.informative}
          />
        </>
      )}
    </button>
  );
};

const SearchCleanButton = ({
  onClick,
  dataCy,
}: Pick<InputActionButtonProps, 'onClick' | 'dataCy'>) => {
  const c = useClassHelper(rootClass);

  return (
    <button
      id="clear-search-input"
      className={`${c(
        '__clear-search-input',
      )} focus-visible:focus-ring-form-inset`}
      type="button"
      onClick={onClick}
      data-cy={dataCy}
    >
      <X size={18} />
    </button>
  );
};

const InputActionButton = (props: InputActionButtonProps) => {
  if (props.inputType === 'password') {
    return <PasswordToggle {...props} data-cy={props.dataCy} />;
  }

  if (props.inputType === 'search') {
    return <SearchCleanButton {...props} data-cy={props.dataCy} />;
  }

  return null;
};

const MemoizedInputField: MemoExoticComponent<React.FC<Props>> = memo(
  ({
    label,
    id,
    icon,
    classes,
    disabled = false,
    error = false,
    errorMessage,
    type = 'text',
    variant = 'default',
    regex,
    readOnly,
    tabIndex = 0,
    name,
    onBlur,
    onFocus,
    onChange,
    app = appTypes.portal,
    required,
    endInputContent,
    touched,
    placeholder,
    onClear,
    description,
    ...rest
  }: Props) => {
    const [inputState, setInputState] = useState({
      showPassword: false,
      focus: false,
    });

    const c = useClassHelper(rootClass);

    const { container: containerClassName, input: inputClassName = '' } =
      classes || {};

    const handlePasswordToggle = () => {
      if (type === 'password') {
        setInputState(prev => ({ ...prev, showPassword: !prev.showPassword }));
      }
    };

    const getInputType = (type: InputTypes) => {
      if (type === 'password') {
        return inputState.showPassword ? 'text' : 'password';
      }

      if (type === 'search') {
        return 'text';
      }

      return type;
    };

    const errorId = `input_${name}_ErrorMessage`;

    const inputWrapperClassName = `${rootClass} ${variants[variant]} ${c({
      '--error': error && app === appTypes.portal,
      '--error-websheet': error && app === appTypes.websheet,
      '--focused': inputState.focus,
      '--readOnly': !!readOnly,
      '--disabled': disabled,
    })} ${cn('border border-gray-300 min-h-10')}`;

    return (
      <div className={containerClassName}>
        {/* Input Label */}
        {typeof label === 'object' && label}
        {typeof label === 'string' && (
          <Label id={id} required={required} baseClass={rootClass}>
            {label}
          </Label>
        )}
        {/* Input Wrapper */}
        <div
          className={c({
            '--input-wrapper-row': Boolean(endInputContent),
          })}
        >
          <div className={inputWrapperClassName}>
            {icon && <div className={c('__icon')}>{icon}</div>}
            <input
              placeholder={placeholder}
              readOnly={readOnly}
              id={id}
              type={getInputType(type)}
              disabled={disabled}
              onBlur={event => {
                if (readOnly) {
                  return;
                }

                setInputState(prev => ({ ...prev, focus: false }));
                onBlur?.(event);
              }}
              onFocus={event => {
                if (readOnly) {
                  return;
                }

                setInputState(prev => ({ ...prev, focus: true }));
                onFocus?.(event);
              }}
              onChange={event => {
                let value = event.target.value;

                if (value) {
                  value = `${value}`.trimStart();
                }

                if (value && regex && !regex.test(value)) {
                  // On a failed regex do not allow input to proceed
                  return;
                }

                onChange?.(event);
              }}
              className={`${c('__input')} ${inputClassName}`}
              tabIndex={tabIndex}
              aria-describedby={errorId}
              name={name}
              {...rest}
            />
            <InputActionButton
              inputType={type}
              isActive={inputState.showPassword}
              onClick={onClear ?? handlePasswordToggle}
              name={name || ''}
              disabled={disabled}
              dataCy={onClear ? 'clear-search' : `toggle-password`}
            />
          </div>
          {endInputContent}
        </div>
        <InputErrorMessage
          id={errorId}
          errorMessage={errorMessage}
          error={error}
          app={app}
          dataCyError={name}
        />
        {description && (
          <Paragraph tag="small" size="sm">
            {description}
          </Paragraph>
        )}
      </div>
    );
  },
);

export const InputField = MemoizedInputField as typeof MemoizedInputField & {
  Label: typeof Label;
  ErrorMessage: typeof InputErrorMessage;
};

InputField.displayName = 'InputField';
InputField.Label = (props: Parameters<typeof Label>[0]) => {
  return <Label {...props} baseClass={rootClass} />;
};

InputField.ErrorMessage = (props: Parameters<typeof InputErrorMessage>[0]) => {
  const { app = appTypes.portal } = props;
  return <InputErrorMessage {...props} app={app} />;
};
